import React, { useState, useEffect, useRef } from 'react';
import { Bell, BellOff, Volume2, VolumeX, AlertTriangle, X } from 'lucide-react';

/**
 * Zone notification hook — monitors price vs zones and fires notifications.
 */
export function useZoneNotifications(zones, currentPrice, symbol, enabled = true) {
  const [notifications, setNotifications] = useState([]);
  const [permission, setPermission] = useState('default');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const lastAlertRef = useRef({}); // Track last alert time per zone to avoid spam

  // Check browser notification permission
  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  const requestPermission = async () => {
    if (!('Notification' in window)) return;
    const result = await Notification.requestPermission();
    setPermission(result);
  };

  // Monitor zones
  useEffect(() => {
    if (!enabled || !zones?.length || !currentPrice) return;

    const now = Date.now();

    for (const zone of zones) {
      const zoneKey = `${zone.start_time}-${zone.bot}-${zone.top}`;
      const lastAlert = lastAlertRef.current[zoneKey] || 0;

      // Don't alert more than once per 5 minutes per zone
      if (now - lastAlert < 5 * 60 * 1000) continue;

      const inZone = currentPrice >= zone.bot && currentPrice <= zone.top;

      if (inZone) {
        lastAlertRef.current[zoneKey] = now;

        const notification = {
          id: `${zoneKey}-${now}`,
          type: 'zone_entry',
          symbol,
          zone,
          price: currentPrice,
          timestamp: now,
          message: `${symbol} entered ${zone.is_supply ? 'SUPPLY' : 'DEMAND'} zone (${zone.grade})`,
        };

        setNotifications(prev => [notification, ...prev].slice(0, 20)); // Keep last 20

        // Browser notification
        if (permission === 'granted') {
          try {
            new Notification(notification.message, {
              body: `Price: ${currentPrice.toFixed(2)} | Zone: ${zone.bot.toFixed(2)} - ${zone.top.toFixed(2)}`,
              icon: '/favicon.ico',
              tag: zoneKey,
            });
          } catch (e) {
            // Notification failed, ignore
          }
        }

        // Sound alert
        if (soundEnabled) {
          playAlertSound(zone.is_supply ? 'warning' : 'info');
        }
      }
    }
  }, [zones, currentPrice, symbol, enabled, permission, soundEnabled]);

  const clearNotifications = () => setNotifications([]);
  const dismissNotification = (id) => setNotifications(prev => prev.filter(n => n.id !== id));

  return {
    notifications,
    permission,
    soundEnabled,
    requestPermission,
    setSoundEnabled,
    clearNotifications,
    dismissNotification,
  };
}

/**
 * Play a short alert sound.
 */
function playAlertSound(type = 'info') {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.connect(gain);
    gain.connect(ctx.destination);

    if (type === 'warning') {
      osc.frequency.value = 880; // Higher pitch for supply zones
      gain.gain.value = 0.1;
    } else {
      osc.frequency.value = 440; // Lower pitch for demand zones
      gain.gain.value = 0.08;
    }

    osc.start();
    osc.stop(ctx.currentTime + 0.15);
  } catch (e) {
    // Audio not available
  }
}

/**
 * Notification panel UI — shows recent zone alerts.
 */
export function ZoneNotificationPanel({
  notifications,
  permission,
  soundEnabled,
  onRequestPermission,
  onToggleSound,
  onClear,
  onDismiss,
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="relative">
      {/* Bell icon with badge */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all border bg-white/80 dark:bg-slate-800/80 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700"
      >
        {permission === 'granted' ? <Bell size={12} /> : <BellOff size={12} />}
        Alerts
        {notifications.length > 0 && (
          <span className="flex items-center justify-center w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold">
            {notifications.length}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {expanded && (
        <>
          {/* Backdrop for mobile */}
          <div className="fixed inset-0 z-40 sm:hidden" onClick={() => setExpanded(false)} />
          <div className="absolute top-full right-0 mt-1 w-[calc(100vw-2rem)] sm:w-80 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-50 max-h-[70vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-slate-100 dark:border-slate-700">
            <h4 className="text-sm font-bold text-slate-900 dark:text-white">Zone Alerts</h4>
            <div className="flex items-center gap-2">
              <button
                onClick={onToggleSound}
                title={soundEnabled ? 'Mute sounds' : 'Enable sounds'}
                className={`p-1.5 rounded-lg transition-colors ${
                  soundEnabled
                    ? 'text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-500/10'
                    : 'text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
              >
                {soundEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
              </button>
              {notifications.length > 0 && (
                <button
                  onClick={onClear}
                  title="Clear all"
                  className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Permission request */}
          {permission !== 'granted' && (
            <div className="p-3 bg-yellow-50 dark:bg-yellow-500/10 border-b border-yellow-100 dark:border-yellow-500/20">
              <p className="text-xs text-yellow-700 dark:text-yellow-400 mb-2">
                Enable browser notifications to get alerts when price enters zones.
              </p>
              <button
                onClick={onRequestPermission}
                className="w-full px-3 py-1.5 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg text-xs font-medium transition-colors"
              >
                Enable Notifications
              </button>
            </div>
          )}

          {/* Notification list */}
          <div className="flex-1 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-xs text-slate-400 dark:text-slate-500">
                No alerts yet. You'll be notified when price enters a zone.
              </div>
            ) : (
              notifications.map(notif => (
                <div
                  key={notif.id}
                  className={`flex items-start gap-2 p-3 border-b border-slate-50 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${
                    notif.type === 'zone_entry' ? 'border-l-2' : ''
                  } ${
                    notif.zone?.is_supply ? 'border-l-red-500' : 'border-l-green-500'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-900 dark:text-white truncate">
                      {notif.message}
                    </p>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                      Price: {notif.price?.toFixed(2)} | Zone: {notif.zone?.bot?.toFixed(2)} - {notif.zone?.top?.toFixed(2)}
                    </p>
                    <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">
                      {new Date(notif.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                  <button
                    onClick={() => onDismiss(notif.id)}
                    className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))
            )}
          </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Visual alert overlay on the chart — flashes when price enters a zone.
 */
export function ZoneVisualAlert({ zone, price }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (zone && price >= zone.bot && price <= zone.top) {
      setVisible(true);
      const timer = setTimeout(() => setVisible(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [zone, price]);

  if (!visible) return null;

  return (
    <div className={`absolute top-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 px-4 py-2 rounded-lg shadow-lg animate-pulse ${
      zone.is_supply
        ? 'bg-red-500/90 text-white'
        : 'bg-green-500/90 text-white'
    }`}>
      <AlertTriangle size={16} />
      <span className="text-sm font-bold">
        {zone.is_supply ? 'SUPPLY' : 'DEMAND'} ZONE — {zone.grade}
      </span>
    </div>
  );
}
